// src/firebase/stripe-checkout-functions.js
// Functions for Stripe Hosted Checkout

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { DEFAULT_STORE_ID } from './firebase';

/**
 * Create Stripe Checkout Session and redirect to hosted checkout
 */
export const createCheckoutSession = async (orderData, customerData, shippingMethodId, promoCode, shippingCost) => {
    try {
        if (!orderData?.items?.length) {
            throw new Error("Cart is empty");
        }

        if (!customerData?.email) {
            throw new Error("Customer email is required");
        }

        // Call the checkout session creation function
        const createSessionFunction = httpsCallable(functions, 'createCheckoutSession');

        // Ensure we have a valid storeId
        const storeId = orderData.storeId || DEFAULT_STORE_ID || 'RR8AXofJByQjhJED7rcQ';


        if (!storeId) {
            throw new Error('Store ID is required for checkout');
        }

        const result = await createSessionFunction({
            cartData: orderData,
            customerData: customerData,
            storeId: storeId,
            shippingMethodId: shippingMethodId,
            promoCode: promoCode,
            shippingCost: shippingCost
        });

        if (result.data.success) {
            return {
                success: true,
                sessionData: {
                    sessionId: result.data.sessionId,
                    sessionUrl: result.data.sessionUrl,
                    amount: result.data.sessionData.amount,
                    currency: result.data.sessionData.currency
                }
            };
        } else {
            throw new Error(result.data.message || 'Failed to create checkout session');
        }

    } catch (error) {
        console.error('Stripe checkout session error:', error);

        if (error.code === 'functions/invalid-argument') {
            throw new Error('Invalid payment data: ' + error.message);
        } else {
            throw new Error(error.message || 'Checkout session creation failed. Please try again.');
        }
    }
};

/**
 * Process order after successful Stripe checkout
 * This would be called from the success page
 */
export const processStripeCheckoutSuccess = async (sessionId) => {
    try {
        if (!sessionId) {
            throw new Error("Session ID is required");
        }

        // Call function to process the successful checkout
        const processSuccessFunction = httpsCallable(functions, 'processStripeCheckoutSuccess');
        const result = await processSuccessFunction({
            sessionId: sessionId
        });

        if (result.data.success) {
            return {
                success: true,
                orderId: result.data.orderId,
                orderData: result.data.orderData
            };
        } else {
            throw new Error(result.data.message || 'Failed to process order');
        }

    } catch (error) {
        console.error('Stripe checkout success processing error:', error);

        if (error.code === 'functions/invalid-argument') {
            throw new Error('Invalid session data: ' + error.message);
        } else {
            throw new Error(error.message || 'Order processing failed. Please contact support.');
        }
    }
};

export default {
    createCheckoutSession,
    processStripeCheckoutSuccess
};